import React, { useState, useEffect, useCallback } from 'react';
import { leavesAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  Check,
  X,
  Eye,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Search,
  Filter,
  User,
  MessageSquare
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LeaveApproval = () => {
  const [applications, setApplications] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [actionType, setActionType] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(false);

  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await leavesAPI.getLeaveApplications(null, selectedStatus);
      setApplications(response.data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [selectedStatus]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const filteredApplications = applications.filter(app =>
    app.employeeId?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.employeeId?.employeeId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewApplication = (application) => {
    setSelectedApplication(application);
    setActionType('view');
    setShowModal(true);
  };

  const handleApproveReject = (application, action) => {
    setSelectedApplication(application);
    setActionType(action);
    setRejectionReason('');
    setComments('');
    setShowModal(true);
  };

  const handleSubmitAction = async () => {
    try {
      const updateData = {
        status: actionType,
        comments: comments
      };

      if (actionType === 'rejected') {
        if (!rejectionReason) {
          toast.error('Rejection reason is required');
          return;
        }
        updateData.rejectionReason = rejectionReason;
      }

      await leavesAPI.updateLeaveApplication(selectedApplication._id, updateData);
      toast.success(`Leave application ${actionType} successfully`);
      setShowModal(false);
      loadApplications();
    } catch (error) {
      toast.error(`Failed to ${actionType} application`);
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'approved':
        return { icon: CheckCircle2, color: 'text-gray-700', bg: 'bg-gray-100', border: 'border-gray-200', label: 'Approved' };
      case 'rejected':
        return { icon: XCircle, color: 'text-gray-700', bg: 'bg-gray-100', border: 'border-gray-200', label: 'Rejected' };
      case 'cancelled':
        return { icon: AlertCircle, color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200', label: 'Cancelled' };
      default:
        return { icon: Clock, color: 'text-gray-700', bg: 'bg-gray-100', border: 'border-gray-200', label: 'Pending Review' };
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const tabs = [
    { id: 'pending', label: 'Pending' },
    { id: 'approved', label: 'Approved' },
    { id: 'rejected', label: 'Rejected' },
    { id: '', label: 'All' },
  ];

  return (
    <div className="">
      <div className=" space-y-6">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-gray-500" />
              Leave Approvals
            </h1>
            <p className="text-slate-500 mt-1">Review and manage employee leave requests</p>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search employee..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full md:w-64 pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm shadow-sm transition-all"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white p-1.5 rounded-lg shadow-sm border border-slate-200 inline-flex flex-wrap gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedStatus(tab.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${selectedStatus === tab.id
                ? 'bg-gray-900 text-white shadow-none'
                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-200 mx-auto"></div>
              <p className="mt-4 text-slate-500">Loading requests...</p>
            </div>
          ) : filteredApplications.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Filter className="w-8 h-8 text-slate-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">No applications found</h3>
              <p className="text-slate-500 text-sm mt-1">Try adjusting your filters or search</p>
            </div>
          ) : (
            <AnimatePresence mode="popLayout">
              {filteredApplications.map((app) => {
                const statusConfig = getStatusConfig(app.status);
                const StatusIcon = statusConfig.icon;

                return (
                  <motion.div
                    key={app._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    layout
                    className="bg-white rounded-lg shadow-sm border border-slate-200 p-5 hover:shadow-md transition-shadow group"
                  >
                    <div className="flex flex-col lg:flex-row gap-5 items-start lg:items-center justify-between">
                      {/* Left Info */}
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-12 h-12 bg-gray-100 rounded-md flex items-center justify-center text-gray-700 font-bold text-lg shrink-0 border border-gray-200">
                          {app.employeeId?.name?.[0]}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-gray-900 text-lg">{app.employeeId?.name}</h3>
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-bold uppercase rounded border border-slate-200">
                              {app.employeeId?.employeeId}
                            </span>
                          </div>

                          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-500">
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: app.leaveTypeId?.color }}></div>
                              <span className="font-medium text-slate-700">{app.leaveTypeId?.name}</span>
                            </div>
                            <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-0.5 rounded-lg border border-slate-100">
                              <Calendar size={14} className="text-slate-400" />
                              <span>{formatDate(app.startDate)} - {formatDate(app.endDate)}</span>
                            </div>
                            <div className="flex items-center gap-1.5 font-bold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-lg border border-gray-200">
                              <Clock size={14} />
                              <span>{app.totalDays} days</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right Actions */}
                      <div className="flex flex-col sm:flex-row items-center gap-4 w-full lg:w-auto border-t lg:border-t-0 border-slate-100 pt-4 lg:pt-0 pl-0 lg:pl-6 lg:border-l">
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${statusConfig.bg} ${statusConfig.border} border`}>
                          <StatusIcon size={14} className={statusConfig.color} />
                          <span className={`text-xs font-bold ${statusConfig.color}`}>{statusConfig.label}</span>
                        </div>

                        <div className="flex items-center gap-2 w-full sm:w-auto">
                          {app.status === 'pending' ? (
                            <>
                              <button
                                onClick={() => handleApproveReject(app, 'approved')}
                                className="flex-1 sm:flex-none px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-semibold rounded-md shadow-none transition-all flex items-center justify-center gap-2"
                              >
                                <Check size={16} /> Approve
                              </button>
                              <button
                                onClick={() => handleApproveReject(app, 'rejected')}
                                className="flex-1 sm:flex-none px-4 py-2 bg-white border border-gray-200 text-gray-700 hover:bg-gray-100 text-sm font-semibold rounded-md transition-all flex items-center justify-center gap-2"
                              >
                                <X size={16} /> Reject
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleViewApplication(app)}
                              className="w-full sm:w-auto px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-semibold rounded-md transition-colors flex items-center justify-center gap-2"
                            >
                              <Eye size={16} /> Details
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Reason Preview */}
                    <div className="mt-4 pt-4 border-t border-slate-100 grid md:grid-cols-2 gap-4">
                      <div className="bg-slate-50/50 p-3 rounded-md border border-slate-100">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-1">Reason</span>
                        <p className="text-sm text-slate-600 line-clamp-1">{app.reason}</p>
                      </div>
                      {app.rejectionReason && (
                        <div className="bg-gray-100 p-3 rounded-md border border-gray-200">
                          <span className="text-[10px] font-bold text-gray-700 uppercase tracking-wide block mb-1">Rejection Reason</span>
                          <p className="text-sm text-gray-700 line-clamp-1">{app.rejectionReason}</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>
      </div>

      {/* Action Modal */}
      <AnimatePresence>
        {showModal && selectedApplication && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
              className="bg-white w-full max-w-lg rounded-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            >
              <div className={`px-4 py-3 flex items-center justify-between border-b ${actionType === 'approved' ? 'bg-gray-100 border-gray-200' :
                actionType === 'rejected' ? 'bg-gray-100 border-gray-200' :
                  'bg-slate-50 border-slate-100'
                }`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${actionType === 'approved' ? 'bg-gray-100 text-gray-700' :
                    actionType === 'rejected' ? 'bg-gray-100 text-gray-700' :
                      'bg-white border border-slate-200 text-slate-600'
                    }`}>
                    {actionType === 'approved' ? <CheckCircle2 size={20} /> :
                      actionType === 'rejected' ? <XCircle size={20} /> :
                        <User size={20} />}
                  </div>
                  <div>
                    <h3 className={`font-bold text-lg ${actionType === 'approved' ? 'text-gray-700' :
                      actionType === 'rejected' ? 'text-gray-700' :
                        'text-slate-900'
                      }`}>
                      {actionType === 'view' ? 'Application Details' :
                        actionType === 'approved' ? 'Approve Request' : 'Reject Request'}
                    </h3>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1.5 hover:bg-black/5 rounded-full transition-colors"
                >
                  <X size={20} className="text-slate-400" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto space-y-6">
                {/* Employee Card */}
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-md border border-slate-100">
                  <div className="w-12 h-12 bg-white border border-slate-200 rounded-full flex items-center justify-center font-bold text-slate-700 text-lg">
                    {selectedApplication.employeeId?.name?.[0]}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{selectedApplication.employeeId?.name}</h4>
                    <p className="text-sm text-slate-500">{selectedApplication.employeeId?.email}</p>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-white border border-slate-200 rounded-md">
                    <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block">Leave Type</label>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: selectedApplication.leaveTypeId?.color }}></div>
                      <span className="font-semibold text-slate-700">{selectedApplication.leaveTypeId?.name}</span>
                    </div>
                  </div>
                  <div className="p-3 bg-white border border-slate-200 rounded-md">
                    <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block">Duration</label>
                    <div className="font-semibold text-slate-700">{selectedApplication.totalDays} days</div>
                  </div>
                  <div className="col-span-2 p-3 bg-gray-100 border border-gray-200 rounded-md flex justify-between items-center">
                    <div>
                      <label className="text-[10px] uppercase font-bold text-gray-700 mb-1 block">From</label>
                      <div className="font-bold text-gray-900">{formatDate(selectedApplication.startDate)}</div>
                    </div>
                    <div className="h-8 w-px bg-gray-100"></div>
                    <div className="text-right">
                      <label className="text-[10px] uppercase font-bold text-gray-700 mb-1 block">To</label>
                      <div className="font-bold text-gray-900">{formatDate(selectedApplication.endDate)}</div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div>
                  <label className="text-xs font-medium text-gray-900 mb-2 flex items-center gap-1.5"><MessageSquare size={14} /> Reason for Leave</label>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-md border border-slate-100">{selectedApplication.reason}</p>
                </div>

                {actionType === 'rejected' && (
                  <div>
                    <label className="text-xs font-bold text-gray-700 mb-2 block">Rejection Reason *</label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      className="w-full p-3 border-2 border-gray-200 rounded-md focus:border-gray-200 focus:ring-4 focus:ring-rose-50 transition-all text-sm"
                      rows="3"
                      placeholder="Why is this request being rejected?"
                    />
                  </div>
                )}

                {(actionType === 'approved' || actionType === 'rejected') && (
                  <div>
                    <label className="text-xs font-bold text-slate-700 mb-2 block">Admin Comments (Optional)</label>
                    <textarea
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      className="w-full p-3 border border-slate-200 rounded-md focus:border-gray-200 focus:ring-4 focus:ring-indigo-50 transition-all text-sm"
                      rows="2"
                      placeholder="Add any notes..."
                    />
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-slate-100 bg-slate-50 flex gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 bg-white border border-slate-200 text-slate-600 font-semibold rounded-md hover:bg-slate-50 transition-colors"
                >
                  {actionType === 'view' ? 'Close' : 'Cancel'}
                </button>
                {actionType !== 'view' && (
                  <button
                    onClick={handleSubmitAction}
                    className={`flex-1 py-3 text-white font-semibold rounded-md shadow-lg transition-all flex items-center justify-center gap-2 ${actionType === 'approved'
                      ? 'bg-gray-900 hover:bg-gray-800 shadow-none'
                      : 'bg-gray-800 hover:bg-gray-900 shadow-none'
                      }`}
                  >
                    {actionType === 'approved' ? (
                      <><CheckCircle2 size={18} /> Confirm Approval</>
                    ) : (
                      <><XCircle size={18} /> Confirm Rejection</>
                    )}
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LeaveApproval;